/**
 * Which placement belongs to which On Body photo, and what kind of piece is on
 * screen. Pure — the panel and `scripts/check-onbody.ts` share it.
 *
 * The photos reach the panel from two places that name them differently:
 *
 *   - production's preset API — iJewel's photos imported by
 *     `scripts/mirror-ijewel-catalogue.mjs`, which keeps the CDN address in
 *     `values.sourceUrl` (spread onto the item by `useManifest`) and names the
 *     preset after the file WITHOUT its extension ("AI_hand_on_chin", or
 *     "photo-jpg" when two files share a stem);
 *   - the dev test manifest — `{ id, label, file }` with the file name as id and
 *     a humanised label ("AI hand on chin").
 *
 * `placements.json` is keyed by the file name and records the CDN address as
 * `source`. So: provenance first (`sourceUrl` / `source` === `source` — exact),
 * then the name, compared as a normalised stem.
 */
import type { PhotoPlacement, PlacementsFile } from "./anchorMath";

/** The fields of a library entry the lookup reads. */
export interface PhotoRef {
  id?: string;
  label?: string;
  file?: string;
  /** A mirrored preset's CDN URL (`values.sourceUrl`). */
  sourceUrl?: string;
  /** A static manifest's provenance field. */
  source?: string;
}

/**
 * "…/files/AI_Hand-On_chin.JPG?x=1" → "ai hand on chin". Drops the query, the
 * folder and the extension — including the mirror's "-jpg" twin suffix — and
 * folds `_`, `-` and spaces together, so a file name, a preset name and a
 * humanised label of the same photo all meet.
 */
export function photoStem(s: string | undefined | null): string {
  if (!s) return "";
  let t = s.trim().split(/[?#]/)[0];
  t = t.split(/[\\/]/).pop() ?? t;
  t = t.replace(/[-.](jpe?g|png|webp|avif|gif)$/i, "");
  return t.toLowerCase().replace(/[\s_-]+/g, " ").trim();
}

export interface PlacementIndex {
  bySource: Map<string, string>;
  byStem: Map<string, string>;
  photos: Record<string, PhotoPlacement>;
}

export function indexPlacements(file: PlacementsFile | null | undefined): PlacementIndex {
  const photos = file?.photos ?? {};
  const bySource = new Map<string, string>();
  const byStem = new Map<string, string>();
  for (const [name, p] of Object.entries(photos)) {
    if (p.source) bySource.set(p.source, name);
    const stem = photoStem(name);
    if (stem && !byStem.has(stem)) byStem.set(stem, name);
  }
  return { bySource, byStem, photos };
}

export interface FoundPlacement {
  /** The placements.json key (the photo's file name). */
  key: string;
  placement: PhotoPlacement;
  /** How it was matched — provenance is exact, a name is a convention. */
  by: "source" | "name";
}

/** The placement for one library photo, or null (a user's own preset). */
export function findPlacement(index: PlacementIndex, ref: PhotoRef): FoundPlacement | null {
  for (const url of [ref.sourceUrl, ref.source]) {
    const key = url ? index.bySource.get(url) : undefined;
    if (key) return { key, placement: index.photos[key], by: "source" };
  }
  for (const name of [ref.file, ref.id, ref.label]) {
    const key = index.byStem.get(photoStem(name));
    if (key) return { key, placement: index.photos[key], by: "name" };
  }
  return null;
}

// ─── What the piece is ──────────────────────────────────────────────────────

export type PieceKind = "ring" | "necklace" | "earring" | "bracelet";

/**
 * Guess the kind of piece from its words — the same rules, in the same order,
 * as the Vault card art (`artFor` in `app/vault/Common.tsx`), so the studio and
 * the Vault never disagree about what a piece is.
 */
export function guessPieceKind(text: string): PieceKind {
  const t = text.toLowerCase();
  if (/neck|pendant|chain/.test(t)) return "necklace";
  if (/ear|hoop|stud/.test(t)) return "earring";
  if (/brace|bangle|cuff|tennis/.test(t)) return "bracelet";
  return "ring";
}

/** On Body has two body parts. Everything that is not a necklace goes on a
 *  finger — the closest fit for a ring, and the panel's default. */
export const bodyPartFor = (k: PieceKind): "finger" | "neck" => (k === "necklace" ? "neck" : "finger");
